import React, { useCallback, useEffect, useRef, useState } from "react";
import Header from "../Header/header";
import StepperHeader from "./StepperHeader";
import AddTreatmentID from "./AddTreatmentID";
import PatientDetails from "./PatientDetails";
import CreateConsentEmail from "./CreateConsentEmail";
import ConfirmAndSend from "./ConfirmAndSend";
import styles from "./flow.module.scss";
import { useLocation } from "react-router-dom";

const steps = [
  { label: "Add treatment ID", sub: "Add the treatment ID to the associated clinician.", component: AddTreatmentID },
  { label: "Patient details", sub: "All patient details are auto-fetched from Dentally.", component: PatientDetails },
  { label: "Create consent email", sub: "Complete the consent email, add media and signatures.", component: CreateConsentEmail },
  { label: "Confirm and send", sub: "Confirm and send consent to patients.", component: ConfirmAndSend },
];

// Clear this key during logout and after the consent workflow is completed.
export const TREATMENT_FLOW_STORAGE_KEY = "treatmentFlow:v1";
const HANDOFF_KEY = "activeTreatmentData";

function readStored(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isTreatmentData(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validStep(value) {
  return Number.isInteger(value) && value >= 0 && value < steps.length;
}

function readRequestedStep(search) {
  const raw = new URLSearchParams(search).get("step");
  if (raw === null || raw.trim() === "") return null;
  const value = Number(raw);
  return validStep(value) ? value : null;
}

function restoreFlow(location) {
  const handoff = readStored(HANDOFF_KEY);
  const saved = readStored(TREATMENT_FLOW_STORAGE_KEY);
  const requestedStep = readRequestedStep(location.search);

  if (isTreatmentData(handoff)) {
    return {
      activeStep: requestedStep ?? 1,
      treatmentData: handoff,
    };
  }

  if (saved?.version === 1 && saved.pathname === location.pathname &&
      isTreatmentData(saved.treatmentData)) {
    return {
      activeStep: validStep(saved.activeStep) ? saved.activeStep : 0,
      treatmentData: saved.treatmentData,
    };
  }

  // Later steps require a treatment; a URL alone cannot supply its data.
  return { activeStep: 0, treatmentData: null };
}

function persistFlow(state, pathname) {
  try {
    window.sessionStorage.setItem(
      TREATMENT_FLOW_STORAGE_KEY,
      JSON.stringify({ version: 1, pathname, ...state })
    );
    return true;
  } catch {
    return false;
  }
}

export default function TreatmentFlow() {
  const location = useLocation();
  const [flowState, setFlowState] = useState(() => restoreFlow(location));
  const stateRef = useRef(flowState);
  const locationKeyRef = useRef(location.key);
  const [storageFailed, setStorageFailed] = useState(false);

  const commit = useCallback((next) => {
    stateRef.current = next;
    setFlowState(next);
    setStorageFailed(!persistFlow(next, location.pathname));
  }, [location.pathname]);

  useEffect(() => {
  const navigated = locationKeyRef.current !== location.key;
  locationKeyRef.current = location.key;

  const requestedStep = readRequestedStep(location.search);
  const handoff = readStored(HANDOFF_KEY);

  let next = stateRef.current;

  if (
    navigated &&
    (location.state?.resetConsentFlow || requestedStep === 0)
  ) {
    next = {
      activeStep: 0,
      treatmentData: null,
    };
  } else if (isTreatmentData(handoff)) {
    next = {
      activeStep: requestedStep ?? 1,
      treatmentData: handoff,
    };
  } else if (navigated && requestedStep !== null) {
    next = {
      ...next,
      activeStep: next.treatmentData ? requestedStep : 0,
    };
  }

  commit(next);

  if (persistFlow(next, location.pathname)) {
    try {
      window.sessionStorage.removeItem(HANDOFF_KEY);
    } catch (error) {
      console.error("Could not clear treatment handoff:", error);
    }
  }
}, [
  location.key,
  location.pathname,
  location.search,
  location.state,
  commit,
]);

  const setTreatmentData = useCallback((value) => {
    const previous = stateRef.current;
    const data = typeof value === "function" ? value(previous.treatmentData) : value;
    commit({ ...previous, treatmentData: data, activeStep: data ? previous.activeStep : 0 });
  }, [commit]);

  const goNext = useCallback(() => {
    const previous = stateRef.current;
    if (!previous.treatmentData) return;
    commit({ ...previous, activeStep: Math.min(previous.activeStep + 1, steps.length - 1) });
  }, [commit]);

  const goBack = useCallback(() => {
    const previous = stateRef.current;
    commit({ ...previous, activeStep: Math.max(previous.activeStep - 1, 0) });
  }, [commit]);

  const { activeStep, treatmentData } = flowState;
  const StepComponent = steps[activeStep].component;
  const reviewUploadSessionId = new URLSearchParams(location.search).get("uploadSessionId") || "";

  return (
    <div className={styles.flowPage}>
      <Header />
      <div className={styles.flowContainer}>
        <StepperHeader steps={steps} activeStep={activeStep} />
        {storageFailed && (
          <p role="alert">Your browser could not save this session. Keep this tab open to avoid losing progress.</p>
        )}
        <div className={styles.flowSection}>
          <header className={styles.flowHeader}>
            <h1 className={styles.flowTitle}>{steps[activeStep].label}</h1>
            <p className={styles.flowDesc}>{steps[activeStep].sub}</p>
          </header>
          <div>
            <StepComponent
              key={`${treatmentData?.id ?? "new"}:${activeStep}:${reviewUploadSessionId}`}
              onNext={goNext}
              onBack={goBack}
              treatmentData={treatmentData}
              setTreatmentData={setTreatmentData}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
